// FAQ ("О сервисе") — ports faq.js: the ChatGPT/Humanteq comparison + the
// accordion. The hand-rolled height-animation accordion becomes a controlled
// single-open list (only one item expanded at a time, as before).

import { useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { COMPARISON, FAQ_ITEMS, type FaqItem } from './faq.data'
import styles from './FAQ.module.css'

export default function FAQ({ items = FAQ_ITEMS, productName = 'Humanteq', comparison = COMPARISON }: { items?: FaqItem[]; productName?: string; comparison?: typeof COMPARISON }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const toggleQuestion = (event: MouseEvent<HTMLButtonElement>) => {
    const index = Number(event.currentTarget.value)
    setOpenIndex(current => current === index ? null : index)
  }

  return (
    <div className={styles.wrap}>
      <section className={styles.compare}>
        <h2 className={styles.h2}>Сравните сами</h2>
        <p className={styles.subtitle}>Один и тот же запрос — два разных ответа</p>

        <div className={styles.grid}>
          <article className={styles.chat}>
            <header className={styles.chatHead}>
              <span className={`${styles.badge} ${styles.badgeGpt}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 0 1-13.3 7.9L3 21l1.1-4.7A9 9 0 1 1 21 12Z" />
                </svg>
              </span>
              <span className={styles.chatName}>ChatGPT</span>
            </header>
            <div className={styles.msg}>
              <span className={`${styles.avatar} ${styles.avatarUser}`}>Я</span>
              <div className={styles.bubble}>{comparison.prompt}</div>
            </div>
            <div className={styles.msg}>
              <span className={`${styles.avatar} ${styles.avatarGpt}`}>GPT</span>
              <div className={styles.bubble}>{comparison.gpt}</div>
            </div>
          </article>

          <article className={`${styles.chat} ${styles.chatHq}`}>
            <header className={styles.chatHead}>
              <span className={`${styles.badge} ${styles.badgeHq}`}>h</span>
              <span className={styles.chatName}>Humanteq</span>
            </header>
            <div className={styles.msg}>
              <span className={`${styles.avatar} ${styles.avatarUser}`}>Я</span>
              <div className={styles.bubble}>{comparison.prompt}</div>
            </div>
            <div className={styles.msg}>
              <span className={`${styles.avatar} ${styles.avatarHq}`}>h</span>
              <div className={styles.bubble}>{comparison.hq}</div>
            </div>
          </article>
        </div>

        <div className={styles.callout}>
          <b>Видите разницу?</b> {comparison.callout}
        </div>
      </section>

      <section className={styles.faq}>
        <h2 className={styles.h2}>Вопросы и ответы</h2>
        <p className={styles.subtitle}>Всё, что обычно спрашивают о {productName}</p>

        <div className={styles.items}>
          {items.map((item, i) => {
            const isOpen = openIndex === i
            return (
              <div key={item.q} className={`${styles.item} ${isOpen ? styles.itemOpen : ''}`}>
                <button
                  className={styles.q}
                  aria-expanded={isOpen}
                  value={i}
                  onClick={toggleQuestion}
                >
                  <span className={styles.qtext}>{item.q}</span>
                  <span className={styles.icon}><span className={styles.plus} /></span>
                </button>
                {isOpen && (
                  <div className={styles.a}>
                    {item.a.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                    {item.link && (
                      <p>
                        {item.link.href.startsWith('/') ? (
                          <Link className={styles.link} to={item.link.href}>{item.link.text}</Link>
                        ) : <a className={styles.link} href={item.link.href} target="_blank" rel="noopener noreferrer">
                          {item.link.text}
                        </a>}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}
